import { composeClassNames } from "@faedev/editor-utils";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState } from "react";
import { Badge, Button, Card } from "react-bootstrap";
import { useCookies } from "react-cookie";
import ReactMarkdown from "react-markdown";
import Paragraph from "src/components/Paragraph";
import Preloader from "src/components/Preloader";
import Translate from "src/components/Translate";
import constants from "src/constants";
import getCookieByName from "src/functions/getCookieByName";
import getLink from "src/functions/getLink";
import translate from "src/functions/translate";
import validateAuthToken from "src/functions/validateAuthToken";
import Small_cancel from "src/icons/Small-cancel";
import { sBadge } from "src/styles/sBadge.module.scss";
import { sCartCard } from "src/styles/sCartCard.module.scss";
import { sCartCardBody } from "src/styles/sCartCardBody.module.scss";

type Props = {
  amountTotal?: number;
  description?: string;
  descriptionNoTrial?: string;
  discount?: string | number;
  itemId?: number;
  imgURL?: { url: string };
  isClient?: boolean;
  isStream?: boolean;
  orderId?: number;
  period?: number;
  price?: number;
  priceBasic?: number;
  setShowItem?: (setShowItem: boolean) => void;
  slug?: string;
  title?: string;
  type?: string;
  gift?: boolean;
};

const CartItem = ({
  amountTotal,
  description,
  descriptionNoTrial,
  discount,
  itemId,
  imgURL,
  isClient,
  isStream,
  orderId,
  period,
  price,
  priceBasic,
  setShowItem,
  slug,
  title,
  type,
  gift,
}: Props) => {
  const { locale: lang } = useRouter();
  const [loading, setLoading] = useState(false);

  const switchType = (type: string) => {
    switch (type) {
      case `lecture`:
        return `class`;
      case `program`:
        return `serie`;
      case `subscription`:
        return `subscription`;
      default:
        return ``;
    }
  };

  const contentType =
    isStream && isStream === true ? "stream" : switchType(type);
  const imageUrl = imgURL !== undefined && imgURL !== null ? imgURL.url : "";
  const isSlug = typeof slug !== "undefined";

  const [useCookie, setCookie, removeCookie] = useCookies(null);

  const handleRemoveItem = async () => {
    setLoading(true);
    const cookieOrderId = getCookieByName(`orderId`);
    return await (async function iife() {
      await (async function iife() {
        var result = await (() =>
          fetch(
            `${constants.Paths.apiUrl}/orders/${
              isClient ? orderId : cookieOrderId
            }/${itemId}`,
            {
              method: "DELETE",
              headers: isClient
                ? validateAuthToken(getCookieByName(`auth-token`), {
                    "Content-Type": "application/json",
                  })
                : { "Content-Type": "application/json" },
            }
          )
            .then(async (res) => {
              if (res.ok) {
                const response = await res.json();
                if (
                  response.order_items.length === 1 &&
                  response.order_items[0].id === itemId
                ) {
                  removeCookie("orderId", constants.CookiesOptions);
                }
              } else {
                throw new Error("API Error");
              }
            })
            .then(() => {
              setShowItem(false);
            }))();
        return result;
      })();
    })();
  };

  const getItemLink = getLink(
    lang,
    contentType === "subscription" ? "prices" : contentType,
    isSlug ? slug : null,
    null
  );

  const getNewDate = (length: number) => {
    const newDate = new Date(
      new Date().setDate(new Date().getDate() + 7 + length)
    );
    const validTill =
      newDate.getDate() +
      ". " +
      (newDate.getMonth() + 1) +
      ". " +
      newDate.getFullYear();
    return validTill;
  };

  const descriptionText = price < 10 ? description : descriptionNoTrial;
  const isDescription =
    typeof descriptionText !== "undefined" && descriptionText?.length > 0;

  return (
    <Card className={composeClassNames([sCartCard, "cart-item"])}>
      {imageUrl && (
        <Link href={getItemLink} passHref locale={false}>
          <Card.Link>
            <Card.Img src={imageUrl} alt={title} />
          </Card.Link>
        </Link>
      )}
      <Card.Body className={composeClassNames([sCartCardBody, "cart-body"])}>
        {title && (
          <Link href={getItemLink} passHref locale={false}>
            <Card.Link>
              <Card.Title>
                {contentType === "subscription" && (
                  <>
                    <Translate tKey={`Subscription`} />:{" "}
                  </>
                )}
                <Translate tKey={title} />
              </Card.Title>
            </Card.Link>
          </Link>
        )}
        {isDescription && contentType === "subscription" && (
          <Paragraph size="sm">
            <ReactMarkdown>
              {descriptionText?.length > 0 &&
                translate(descriptionText, lang)
                  .replace("%SUB_NAME%", translate(title, lang))
                  .replace(
                    "%SUB_TRIAL%",
                    String(constants.Finance.subscriptionTrialPeriod)
                  )
                  .replace(
                    "%SUB_END%",
                    getNewDate(constants.Finance.subscriptionTrialPeriod)
                  ) // TODO: period should be send
                  .replace("%SUB_PRICE%", String(price))}
            </ReactMarkdown>
          </Paragraph>
        )}
        {amountTotal > 0 && <Card.Text>{amountTotal}</Card.Text>}
        <Card.Text>
          <div className="card-badges">
            {contentType != "" && (
              <Badge
                pill={true}
                variant="secondary"
                className={composeClassNames([
                  sBadge,
                  "badge-outline",
                  "badge-program",
                ])}
              >
                {contentType === "class" && <Translate tKey={`Class`} />}
                {contentType === "serie" && <Translate tKey={`Serie`} />}
                {contentType === "stream" && <Translate tKey={`Stream`} />}
                {contentType === "subscription" && (
                  <Translate
                    tKey={
                      price !== constants.Finance.subscriptionTrialPrice
                        ? "Subscription"
                        : "Subscription trial"
                    }
                  />
                )}
              </Badge>
            )}
            {discount && price !== constants.Finance.subscriptionTrialPrice && (
              <Badge
                pill={true}
                variant="secondary"
                className={composeClassNames([sBadge, "badge-discount"])}
              >
                -{discount}%
              </Badge>
            )}
          </div>
          <div className="card-prices">
            {priceBasic && (
              <span className="card-price card-price-basic">
                {priceBasic} <Translate tKey={`CZK`} />
              </span>
            )}
            <span className="card-price">
              {price} <Translate tKey={`CZK`} />
            </span>
          </div>
        </Card.Text>
        <Button
          variant="link"
          onClick={handleRemoveItem}
          size="sm"
          className={`btn-delete`}
        >
          {loading ? <Preloader size="sm" /> : <Small_cancel />}
        </Button>
      </Card.Body>
    </Card>
  );
};

export default CartItem;
